import { motion } from "framer-motion";
import { BadgeCheck, CalendarClock, EyeOff, Sparkles } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { SHOTS } from "@/lib/site";

const Card = ({ testid, className = "", children, delay = 0 }) => (
  <Reveal delay={delay} className={className}>
    <motion.div
      data-testid={testid}
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className="flex h-full flex-col overflow-hidden rounded-[28px] border border-hairline bg-cream p-7 shadow-[0_20px_45px_-30px_rgba(36,26,22,0.25)] sm:p-9"
    >
      {children}
    </motion.div>
  </Reveal>
);

const WeekChips = () => {
  const days = [
    ["M", "12h"], ["T", "12h"], ["W", "OFF"], ["T", "Night"], ["F", "OFF"], ["S", "12h"], ["S", "OFF"],
  ];
  return (
    <div className="mt-auto grid grid-cols-7 gap-1.5 pt-6">
      {days.map(([d, s], i) => (
        <div
          key={i}
          className={`rounded-xl border px-1 py-2.5 text-center ${
            s === "OFF" ? "border-terracotta/30 bg-peach/60" : "border-hairline bg-sand"
          }`}
        >
          <div className="text-[10px] font-bold text-clay">{d}</div>
          <div className={`mt-0.5 text-[10px] font-extrabold ${s === "OFF" ? "text-terracotta-dark" : "text-ink/70"}`}>
            {s}
          </div>
        </div>
      ))}
    </div>
  );
};

export const Bento = () => (
  <section id="features" data-testid="features-section" className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
    <Reveal className="max-w-2xl">
      <Eyebrow>Why STETH</Eyebrow>
      <h2 className="mt-5 font-editorial text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
        Built around{" "}
        <span className="font-normal italic text-terracotta-dark">healthcare life</span>
      </h2>
      <p className="mt-5 text-base leading-relaxed text-clay sm:text-lg">
        Generic dating apps don't understand a 12-hour shift or a pager at 3am.
        STETH does, down to the last detail.
      </p>
    </Reveal>

    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-7">
      <Card testid="bento-feature-npi" className="md:col-span-8" delay={0.05}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-extrabold tracking-tight sm:text-2xl">License & NPI verification</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-clay">
              Submit a license or work ID and earn the badge that says you're the
              real deal, so every match starts on trust.
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sage px-3.5 py-2 text-[11px] font-extrabold text-ink">
            <BadgeCheck size={14} /> Verified Healthcare Pro
          </span>
        </div>
        <div className="mt-auto pt-8">
          <div className="rounded-2xl bg-gradient-to-br from-[#EEF5F1] to-[#F6EFE8] p-5">
            <svg viewBox="0 0 520 90" className="w-full" fill="none" aria-hidden="true">
              <path
                d="M0 45 H120 L140 45 155 15 175 75 190 45 H300 L315 45 330 20 348 70 362 45 H520"
                stroke="#81B29A" strokeOpacity="0.3" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              />
              <path
                d="M0 45 H120 L140 45 155 15 175 75 190 45 H300 L315 45 330 20 348 70 362 45 H520"
                stroke="#4E8B6C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="ekg-run"
              />
            </svg>
            <div className="mt-3 flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-clay">
              <span>Credential check</span>
              <span className="text-sage">Passed · 12s ago</span>
            </div>
          </div>
        </div>
      </Card>

      <Card testid="bento-feature-schedule" className="md:col-span-4" delay={0.12}>
        <CalendarClock size={26} className="text-terracotta" />
        <h3 className="mt-4 text-xl font-extrabold tracking-tight">Off-call schedule sync</h3>
        <p className="mt-2 text-sm leading-relaxed text-clay">
          Share your rota, not your excuses. We surface matches whose free
          nights actually line up with yours.
        </p>
        <WeekChips />
      </Card>

      <Card testid="bento-feature-prompts" className="md:col-span-5" delay={0.05}>
        <Sparkles size={26} className="text-sunny" />
        <h3 className="mt-4 text-xl font-extrabold tracking-tight">Zero-work-talk mode</h3>
        <p className="mt-2 text-sm leading-relaxed text-clay">
          Optional prompts keep first dates about{" "}
          <span className="font-editorial italic">you</span>, not the ward.
        </p>
        <div className="mt-auto flex flex-wrap gap-2 pt-6">
          {["Best post-shift comfort food?", "Coffee before rounds, or after?", "Dream vacation between contracts?"].map((p) => (
            <span key={p} className="rounded-full border border-hairline bg-sand px-3.5 py-2 text-xs font-semibold text-ink/80">
              {p}
            </span>
          ))}
        </div>
      </Card>

      <Card testid="bento-feature-stealth" className="md:col-span-7" delay={0.12}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <EyeOff size={26} className="text-terracotta" />
            <h3 className="mt-4 text-xl font-extrabold tracking-tight">Colleague stealth shield</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-clay">
              Blur your profile from coworkers within your hospital's radius.
              Date freely. The break room never has to know.
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-hairline bg-sand px-3 py-1.5 text-[11px] font-extrabold text-ink">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-sage" />
            </span>
            Shield on
          </span>
        </div>
        <div className="mt-auto flex items-center gap-3 pt-8">
          {[SHOTS.verified, SHOTS.firstdate, SHOTS.onboarding].map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className="h-14 w-14 rounded-2xl border border-hairline object-cover blur-[5px] saturate-50"
            />
          ))}
          <span className="ml-1 text-xs font-bold text-clay">Hidden from St. Mary's · 3 mi radius</span>
        </div>
      </Card>
    </div>
  </section>
);
